/**
 * 이용권 스토어 전용 API 엔드포인트 (2안 v2: 같은 웹, 라우트 분리)
 *
 * - `/store/*` : store-api (게이트웨이 경로 라우팅). **스토어 전용 토큰(Bearer)** 필요, /s/ 암호화 채널 미사용
 * - `/s/user/*`, `/auth/*` : 의료 서버 (기존 클라이언트·쿠키·암호화 채널)
 * 모든 결제 요청·승인은 서버 감사 로그(store_log)에 기록된다.
 */
export class StoreURLList {
  static AUTH = {
    /** [POST] 스토어 전용 토큰 발급 — 의료 서버, 로그인 쿠키 필요 (별도 키쌍·15분·hospitalCode 포함) */
    STORE_TOKEN: `${import.meta.env.VITE_API_BASE_URL}/auth/store/token`,
  };

  static PRODUCT = {
    /** [GET] 판매 상품 목록 (가격은 반드시 서버 기준) */
    LIST: `${import.meta.env.VITE_API_BASE_URL}/store/products`,
  };

  static BRANDPAY = {
    /** [GET] store — 로그인 의료진 본인의 브랜드페이 customerKey (없으면 서버가 생성) */
    CUSTOMER: `${import.meta.env.VITE_API_BASE_URL}/store/brandpay/customer`,
    /** [GET] store — 본인이 브랜드페이에 등록한 결제수단(카드) 목록 */
    METHODS: `${import.meta.env.VITE_API_BASE_URL}/store/brandpay/methods`,
    /** [GET] store — 비인증. 리다이렉트 페이지가 code 를 Access Token 으로 교환하도록 서버에 요청 */
    CALLBACK: (code: string, customerKey: string) =>
      `${import.meta.env.VITE_API_BASE_URL}/store/brandpay/callback?code=${encodeURIComponent(code)}&customerKey=${encodeURIComponent(customerKey)}`,
  };

  static GUARDIAN = {
    /**
     * [POST] 보호자 이름+전화번호로 보호자/자녀 조회 — **의료 서버(user 모듈)** API.
     * 자녀는 요청 의료진 소속 병원 범위로 제한, 전화번호 마스킹 응답. 없으면 { guardian: null }
     */
    SEARCH: `${import.meta.env.VITE_API_BASE_URL}/s/user/web/guardian/search`,
  };

  static VOUCHER = {
    /** [POST] 의료 서버 — store 영수증으로 치료 이용권 자격 발급 (멱등) */
    CLAIM: `${import.meta.env.VITE_API_BASE_URL}/s/treatment/voucher/claim`,
    /** [GET] store — 영수증 재발급 (결제됐지만 자격 발급을 못 마친 주문) */
    RECEIPT: (orderId: string) =>
      `${import.meta.env.VITE_API_BASE_URL}/store/orders/${encodeURIComponent(orderId)}/receipt`,
    /** [PATCH] store — 자격 발급 완료 표시 (표시용) */
    CLAIMED: (orderId: string) =>
      `${import.meta.env.VITE_API_BASE_URL}/store/orders/${encodeURIComponent(orderId)}/claimed`,
  };

  static REFUND = {
    /** [POST] 의료 서버 — 환불 증서 발급 (미사용 이용권 잠금) */
    ATTESTATION: `${import.meta.env.VITE_API_BASE_URL}/s/treatment/voucher/refund-attestation`,
    /** [POST] 의료 서버 — 환불 중단 시 잠금 해제 */
    CANCEL: `${import.meta.env.VITE_API_BASE_URL}/s/treatment/voucher/refund-cancel`,
    /** [POST] store — 환불 견적 (증서 제출 → 규칙·금액, 부작용 없음) */
    QUOTE: (orderId: string) =>
      `${import.meta.env.VITE_API_BASE_URL}/store/orders/${encodeURIComponent(orderId)}/refund/quote`,
    /** [POST] store — 환불 실행 (증서 제출 → PG 취소(부분 가능) → 환불 영수증) */
    ORDER: (orderId: string) =>
      `${import.meta.env.VITE_API_BASE_URL}/store/orders/${encodeURIComponent(orderId)}/refund`,
    /** [POST] store — 예외 환불 요청 (SA 관리자 승인 대상) */
    REQUEST: (orderId: string) =>
      `${import.meta.env.VITE_API_BASE_URL}/store/orders/${encodeURIComponent(orderId)}/refund-requests`,
    /** [GET] store 관리자 — 예외 환불 요청 목록 (?status=) */
    ADMIN_REQUESTS: `${import.meta.env.VITE_API_BASE_URL}/store/admin/refund-requests`,
    /** [PATCH] store 관리자 — 요청 승인/거절 */
    ADMIN_DECIDE: (requestId: number) =>
      `${import.meta.env.VITE_API_BASE_URL}/store/admin/refund-requests/${requestId}`,
    /** [POST] 의료 서버 — 환불 영수증으로 자격 회수 */
    REVOKE: `${import.meta.env.VITE_API_BASE_URL}/s/treatment/voucher/revoke`,
  };

  static PURCHASE = {
    /** [POST] 위젯(일반) 결제용 주문 생성 (서버 금액 산출) */
    PREPARE: `${import.meta.env.VITE_API_BASE_URL}/store/purchase/prepare`,
    /**
     * [POST] 위젯(일반) 결제 승인 + 이용권 발급 (원자 처리, 금액 위변조 검증)
     * 결제 승인 → 환자 앞 이용권 발급(미사용). 실패 시 결제 자동 취소.
     */
    CONFIRM: `${import.meta.env.VITE_API_BASE_URL}/store/purchase/confirm`,
  };

  static SETTLEMENT = {
    /**
     * [GET] 관리자(에스알파) 활성화 기록·수수료 정산 조회 (?month=YYYY-MM)
     * SuperAdmin 전용 — 파트너사 수수료 정산 근거 데이터.
     */
    LIST: `${import.meta.env.VITE_API_BASE_URL}/store/admin/settlements`,
  };

  static PAYMENT_HISTORY = {
    /**
     * [GET] 결제 내역 조회 (?page=&size=&status=&q=)
     * - 병원 범위는 스토어 토큰의 hospitalCode 로 서버가 강제한다 (클라이언트가 지정하지 않음).
     * - 환자 이름은 서버에서 마스킹하여 반환한다 (원본 미제공, 개인정보 최소화).
     * - 페이지네이션·필터·합계는 서버에서 계산한다.
     */
    LIST: `${import.meta.env.VITE_API_BASE_URL}/store/payments/history`,
  };
}
